/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import ButtonWithLoading from "components/ButtonWithLoading";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@material-ui/core";
import { validatePassword } from "@dashboard-v3/shared";
import useValidation from "utils/useValidation";
import { isBlank } from "utils/string";
import isEmail from "utils/isEmail";
import { StyledTextField } from "./Styled";
import PasswordSection from "./PasswordSection";
import { FormFields, getLoginOptions, isPasswordExclusive } from "./utils";

import type {
  LoginOptionNames,
  UserResource,
  UserRole,
} from "@dashboard-v3/api";
import SelectField from "components/Forms/SelectField";
import { getUser } from "utils/authentication";

export type UserSaveProps = {
  id?: string;
  email: string;
  name: string;
  password?: string;
  role: UserRole;
};

export type UserFormProps = {
  isOpen: boolean;
  user?: UserResource;
  onClose: () => void;
  onSave: (user: UserSaveProps) => Promise<void>;
};

const USER_ROLE_OPTIONS: UserRole[] = ["OWNER", "ADMIN"];

export default function UserFormModal(props: UserFormProps) {
  const { isOpen, onClose, onSave, user = null } = props;
  const { t } = useTranslation("users");
  const { enqueueSnackbar } = useSnackbar();
  const [fields, setFields] = useState<FormFields>(initialValues(user));
  const [loginOptions, setLoginOptions] = useState<LoginOptionNames[]>([]);
  const [loading, setLoading] = useState(false);
  const { errors, validate, clearErrors, validateAll } = useValidation(fields);
  const authUser = getUser();
  const isEditing = !!user;

  useEffect(() => {
    const fetchLoginOptions = async () => {
      try {
        const options = await getLoginOptions();
        setLoginOptions(options);
      } catch (e) {
        enqueueSnackbar(t("error.loginOptions.fetch"), { variant: "error" });
        onClose();
      }
    };
    fetchLoginOptions();
  }, []);

  const updateFormFields = (updates: Partial<FormFields>) => {
    setFields(prev => ({ ...prev, ...updates }));
  };

  const handleOnChange = e => {
    const { name, value, checked } = e.target;
    clearErrors(name);
    updateFormFields({
      [name]: name === "enablePassword" ? checked : value,
      isSubmitting: false,
    });
  };

  const handleOnBlur = e => {
    const { name, value } = e.target;

    if (formValidations[name] && !errors[name]) {
      validate(name, formValidations[name]);
    }

    updateFormFields({ [name]: value.trim() });
  };

  const formValidations = {
    name: value => {
      const field = t("name");
      if (isBlank(value)) {
        return t("error.common.required", { field });
      }
      if (value.length < 2) {
        return t("error.common.length", { field, length: "2" });
      }
    },
    email: value => {
      if (isBlank(value)) {
        return t("error.common.required", { field: t("common:email") });
      }
      if (!isEmail(value)) {
        return t("error.email.format");
      }
    },
    confirmPassword: value => {
      if (isBlank(value)) {
        return t("error.common.required", { field: t("confirmPassword") });
      }
      if (value !== fields.password) {
        return t("error.confirmPassword.match");
      }
    },
  };

  const saveEdited = async () => {
    updateFormFields({ isSubmitting: true });
    const { name, password, role } = fields;
    const changes: UserSaveProps = {
      name,
      role,
      id: user.id,
      email: user?.email,
      ...(password && { password }),
    };
    let isValid = false;
    if (fields.enablePassword) {
      const [isValidPassword] = validatePassword(password);
      const fieldsValidation = validateAll(formValidations, [
        "name",
        "email",
        "confirmPassword",
      ]);
      isValid = isValidPassword && fieldsValidation;
    } else {
      isValid = validateAll(formValidations, ["name"]);
    }

    if (isValid) {
      setLoading(true);
      try {
        await onSave(changes);
        onClose();
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const saveNew = async () => {
    updateFormFields({ isSubmitting: true });
    const { name, email, password, role } = fields;
    let isValid = false;

    if (isPasswordExclusive(loginOptions) || fields.enablePassword) {
      const [isValidPassword] = validatePassword(password);
      const fieldsValidation = validateAll(formValidations, [
        "name",
        "email",
        "confirmPassword",
      ]);

      isValid = isValidPassword && fieldsValidation;
    } else {
      isValid = validateAll(formValidations, ["name", "email"]);
    }

    if (isValid) {
      setLoading(true);
      try {
        await onSave({ name, email, password, role });
        onClose();
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFormSubmit = async e => {
    e.preventDefault();
    if (isEditing) {
      await saveEdited();
    } else {
      await saveNew();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      onKeyDown={e => e.stopPropagation()}
      fullWidth
    >
      <DialogTitle>{isEditing ? t("editUser") : t("addUser")}</DialogTitle>
      <form onSubmit={handleFormSubmit}>
        <DialogContent style={{ paddingTop: "8px" }}>
          <StyledTextField
            variant="outlined"
            fullWidth
            name="name"
            label={t("name")}
            error={!!errors.name}
            helperText={errors.name}
            value={fields.name}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            inputProps={{
              "data-testid": "user-name-input",
            }}
          />
          <StyledTextField
            variant="outlined"
            fullWidth
            name="email"
            label={t("common:email")}
            error={!!errors.email}
            helperText={errors.email}
            value={fields.email}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            disabled={!!isEditing}
            inputProps={{
              "data-testid": "user-email-input",
            }}
          />
          <SelectField
            name="role"
            label={t("role")}
            options={USER_ROLE_OPTIONS}
            value={fields.role}
            onChange={handleOnChange}
            style={{ marginBottom: 30 }}
            renderOption={r => (
              <MenuItem
                key={r}
                value={r}
                disabled={
                  !(allowedRolesForUser.get(authUser?.role) ?? []).includes(r)
                }
              >
                {t(`userRole.${r}`) as string}
              </MenuItem>
            )}
          />

          {loginOptions.includes("PASSWORD") && (
            <PasswordSection
              user={user}
              loginOptions={loginOptions}
              fields={fields}
              errors={errors}
              isSubmitting={fields.isSubmitting}
              onChange={handleOnChange}
              onBlur={handleOnBlur}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="default"
            data-testid="users__cancel-user-add"
            disableElevation
            onClick={onClose}
            disabled={loading}
          >
            {t("common:cancel")}
          </Button>
          <ButtonWithLoading
            data-testid={isEditing ? "save-user-btn" : "add-user-btn"}
            color="primary"
            disableElevation
            type="submit"
            variant="contained"
            loading={loading}
            onClick={() => {}}
          >
            {isEditing ? t("common:save") : t("common:add")}
          </ButtonWithLoading>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const initialValues = (user?: UserFormProps["user"]) => ({
  name: user?.name || "",
  email: user?.email || "",
  password: "",
  role: user?.role || ("ADMIN" as UserRole),
  confirmPassword: "",
  enablePassword: false,
  isSubmitting: false,
});

const allowedRolesForUser = new Map<UserRole, UserRole[]>([
  ["OWNER", ["OWNER", "ADMIN"]],
  ["ADMIN", ["ADMIN"]],
]);
